import { useState, useEffect, useRef } from "react";

interface UseSearchHighlightOptions<T> {
  searchQuery?: string | undefined;
  items: T[];
  isCurrentSearchResult?: boolean | undefined;
  currentOccurrenceIndex?: number | undefined;
  getTextFromItem: (item: T) => string | undefined;
}

interface UseSearchHighlightReturn {
  highlightedIndices: number[];
  currentHighlightIndex: number;
  itemRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  getHighlightClass: (idx: number) => string;
}

export const useSearchHighlight = <T>({
  searchQuery,
  items,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  getTextFromItem,
}: UseSearchHighlightOptions<T>): UseSearchHighlightReturn => {
  const [highlightedIndices, setHighlightedIndices] = useState<number[]>([]);
  const [currentHighlightIndex, setCurrentHighlightIndex] =
    useState<number>(-1);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!searchQuery || !items.length) {
      setHighlightedIndices([]);
      setCurrentHighlightIndex(-1);
      return;
    }

    const query = searchQuery.toLowerCase();
    const matchingIndices: number[] = [];
    let occurrenceIndex = 0;

    items.forEach((item, index) => {
      const text = getTextFromItem(item);
      if (text && text.toLowerCase().includes(query)) {
        matchingIndices.push(index);
        if (
          isCurrentSearchResult &&
          currentOccurrenceIndex === occurrenceIndex
        ) {
          setCurrentHighlightIndex(index);
        }
        occurrenceIndex++;
      }
    });

    setHighlightedIndices(matchingIndices);

    if (
      isCurrentSearchResult &&
      currentOccurrenceIndex !== undefined &&
      matchingIndices.length > 0
    ) {
      const targetIndex = matchingIndices[currentOccurrenceIndex];
      if (targetIndex !== undefined && itemRefs.current[targetIndex]) {
        const timeoutId = setTimeout(() => {
          itemRefs.current[targetIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    searchQuery,
    items,
    isCurrentSearchResult,
    currentOccurrenceIndex,
    getTextFromItem,
  ]);

  const getHighlightClass = (idx: number): string => {
    if (idx === currentHighlightIndex) {
      return "!bg-[#bad5fd] !border-2 !border-blue-500 rounded-sm shadow-[0_0_0_2px_rgba(59,130,246,0.3)] animate-[pulse_0.5s_ease-in-out]";
    }
    if (highlightedIndices.includes(idx)) {
      return "!bg-[#fdfdff] !border-2 !border-[#8b8c8b] rounded-sm shadow-[0_0_0_2px_rgba(139,140,139,0.2)]";
    }
    return "";
  };

  return {
    highlightedIndices,
    currentHighlightIndex,
    itemRefs,
    getHighlightClass,
  };
};
